import { BLE_IN_RANGE_PLACEHOLDER_METERS, type PeerPresenceKind } from './peerDisplay';

/** Max placement radius from {@link distanceToRadius} (meters > 60). */
export const RADAR_MAX_VISUAL_RADIUS = 150;
/** Outermost peer decoration at placement (tracked ring — matches PEER_TRACKED_RING_R in UI). */
export const RADAR_PEER_OUTER_EXTENT = 20;
/** Minimum inset from viewBox edge — just enough to avoid anti-aliased clip. */
export const RADAR_VIEWBOX_EDGE_BUFFER = 2;
/** @deprecated Use {@link RADAR_VIEWBOX_EDGE_BUFFER}; kept for clarity in reports. */
export const RADAR_VIEWBOX_MARGIN = RADAR_VIEWBOX_EDGE_BUFFER;

export const RADAR_RING_OUTER = 140;
export const RADAR_RING_MID = 95;
export const RADAR_RING_INNER = 50;
export const RADAR_CLUSTER_THRESHOLD = 40;
/** Scaled with Lynx 400px radar (was 16 at 340px). */
export const RADAR_AVATAR_RADIUS = 19;
export const RADAR_ARROW_START_OFFSET = 18;
export const RADAR_ARROW_END_GAP = 6;
export const RADAR_MIN_ARROW_LENGTH = 10;
export const RADAR_MIN_ARROW_ROOM = 6;

/** Max-radius peer center + outer decoration + edge buffer. */
export const RADAR_CENTER =
  RADAR_MAX_VISUAL_RADIUS + RADAR_PEER_OUTER_EXTENT + RADAR_VIEWBOX_EDGE_BUFFER;
export const RADAR_VIEWBOX_SIZE = RADAR_CENTER * 2;

export type RingLevel = 'none' | 'dim' | 'bright';

export interface RingHighlights {
  outer: RingLevel;
  mid: RingLevel;
  inner: RingLevel;
}

export interface RadarGeometryPeer {
  id: string;
  distanceMeters: number | null;
  bearingDeg: number | null;
  presenceKind: PeerPresenceKind;
  uwbActive?: boolean;
  uwbDistance?: number | null;
  directionAvailable?: boolean;
  /** Degrees the bearing could be off (see PeerDisplayInfo); null when unknown. */
  bearingUncertaintyDeg?: number | null;
  horizontalAccuracyMeters?: number | null;
  /** Where the bearing came from (PeerDisplayInfo.bearingSource); null with no bearing. */
  bearingSource?: 'uwb' | 'gps' | null;
}

/** Row shape shared by live engine output and findPeerSnapshot restore (no nested `peer`). */
export type FindPeerRowForRadar = {
  peerId: string;
  displayName: string;
  photoUrl?: string | null;
  /** A map amenity a Find chip put on the radar (its map kind): drawn as the map's disc, tracked like a friend. */
  amenityKind?: string | null;
  bearingDeg?: number | null;
  uwbActive?: boolean;
  uwbDistance?: number | null;
  uwbDirectionAvailable?: boolean;
  display: {
    kind: PeerPresenceKind;
    distanceMeters: number | null;
    gpsLastKnownAt?: number | null;
    horizontalAccuracyMeters?: number | null;
    bearingUncertaintyDeg?: number | null;
    bearingSource?: 'uwb' | 'gps' | null;
  };
};

export type RadarDisplayPeer = RadarGeometryPeer & {
  displayName: string;
  photoUrl: string | null;
  amenityKind: string | null;
  gpsLastKnownAt: number | null;
  isTracked: boolean;
  horizontalAccuracyMeters: number | null;
};

export function mapFindRowsToRadarPeers(
  rows: FindPeerRowForRadar[],
  trackedPeerId: string | null,
): RadarDisplayPeer[] {
  return rows.map((row) => ({
    id: row.peerId,
    displayName: row.displayName,
    photoUrl: row.photoUrl ?? null,
    amenityKind: row.amenityKind ?? null,
    distanceMeters: row.display.distanceMeters,
    bearingDeg: row.bearingDeg ?? null,
    presenceKind: row.display.kind,
    gpsLastKnownAt: row.display.gpsLastKnownAt ?? null,
    uwbActive: row.uwbActive === true,
    uwbDistance: row.uwbDistance ?? null,
    directionAvailable: row.uwbDirectionAvailable === true,
    isTracked: row.peerId === trackedPeerId,
    horizontalAccuracyMeters: row.display.horizontalAccuracyMeters ?? null,
    bearingUncertaintyDeg: row.display.bearingUncertaintyDeg ?? null,
    // Restored snapshot rows predate bearingSource: a uwb-live row's bearing is UWB's, any other kind's is GPS's.
    bearingSource:
      row.display.bearingSource !== undefined
        ? row.display.bearingSource
        : row.bearingDeg != null
          ? row.display.kind === 'uwb-live'
            ? 'uwb'
            : 'gps'
          : null,
  }));
}

export interface PositionedRadarPeer {
  peer: RadarGeometryPeer;
  screenX: number;
  screenY: number;
}

export interface RadarCluster {
  centerX: number;
  centerY: number;
  peers: PositionedRadarPeer[];
}

export interface TargetArrowGeometry {
  visible: boolean;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Where the stem ends: the back of the head (x2/y2 is the tip). */
  stemX2?: number;
  stemY2?: number;
  tipPath: string;
}

function isRadarDebugEnabled(): boolean {
  try {
    const meta = import.meta as ImportMeta & { env?: { DEV?: boolean } };
    return meta.env?.DEV === true;
  } catch {
    return false;
  }
}

/**
 * What the drawn arrow is aiming at, from the same resolved target the radar's spring chases (radarMotion
 * resolveRadarTarget). The old diagnostics recomputed position and visibility with the pre-spring rules, so they
 * could log an arrow the radar did not draw (2026-10-16 audit).
 */
export interface ArrowDiagnosticsTarget {
  radius: number;
  angle: number;
  arrow: number;
  directionWeight: number;
  bearingDeg: number | null;
  bearingFrom: 'live' | 'held' | null;
}

export function logTargetArrowDiagnostics(
  peer: RadarGeometryPeer,
  deviceHeadingDeg: number | null,
  target: ArrowDiagnosticsTarget,
): void {
  if (!isRadarDebugEnabled()) {
    return;
  }
  // The same geometry and opacity the frame loop paints (TrackingRadar useRadarMotion), at the target.
  const geometry = computeTargetArrow({
    peerScreenX: 0,
    peerScreenY: 0,
    visualRadius: target.radius,
    screenAngleRad: target.angle,
    show: true,
  });
  const opacity = geometry.visible ? target.arrow * targetArrowRoomFactor(target.radius) : 0;
  console.info('[radar] tracked peer arrow diagnostics', {
    peerId: peer.id,
    presenceKind: peer.presenceKind,
    peerBearingDeg: peer.bearingDeg,
    bearingSource: peer.bearingSource ?? null,
    drawnBearingDeg: target.bearingDeg,
    bearingFrom: target.bearingFrom,
    directionWeight: target.directionWeight,
    distanceMeters: peer.distanceMeters,
    uwbActive: peer.uwbActive === true,
    uwbDistance: peer.uwbDistance ?? null,
    directionAvailable: peer.directionAvailable === true,
    placementMeters: radarPlacementMeters(peer),
    visualRadius: target.radius,
    deviceHeadingDeg,
    arrowOpacity: opacity,
    arrowVisible: opacity > 0.01,
    arrowLength: geometry.visible
      ? Math.hypot(geometry.x2 - geometry.x1, geometry.y2 - geometry.y1)
      : 0,
  });
}

function warnNonFiniteRadarPosition(
  peer: RadarGeometryPeer,
  deviceHeadingDeg: number | null,
  details: { x: number; y: number; visualRadius: number },
): void {
  if (!isRadarDebugEnabled()) {
    return;
  }
  console.warn('[radar] computeScreenPosition non-finite result — falling back to center', {
    peerId: peer.id,
    bearingDeg: peer.bearingDeg,
    distanceMeters: peer.distanceMeters,
    presenceKind: peer.presenceKind,
    deviceHeadingDeg,
    visualRadius: details.visualRadius,
    computedX: details.x,
    computedY: details.y,
  });
}

function safeCenterFallback(): { x: number; y: number } {
  return { x: RADAR_CENTER, y: RADAR_CENTER };
}

/**
 * Radius knots: the rings sit at 5 / 15 / 30 m, so those distances must land exactly on them. Between knots the curve
 * is a monotone cubic (Fritsch–Carlson), not straight lines: the old piecewise-linear map changed speed at every
 * ring, so a friend walking steadily in or out visibly lurched as they crossed one (Turnstile, 2026-10-14).
 */
const RADIUS_KNOTS: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [5, RADAR_RING_INNER],
  [15, RADAR_RING_MID],
  [30, RADAR_RING_OUTER],
  [60, RADAR_MAX_VISUAL_RADIUS],
];

const RADIUS_TANGENTS: number[] = (() => {
  const n = RADIUS_KNOTS.length;
  const h: number[] = [];
  const delta: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    h.push(RADIUS_KNOTS[i + 1][0] - RADIUS_KNOTS[i][0]);
    delta.push((RADIUS_KNOTS[i + 1][1] - RADIUS_KNOTS[i][1]) / h[i]);
  }
  const m = new Array<number>(n).fill(0);
  for (let i = 1; i < n - 1; i += 1) {
    if (delta[i - 1] * delta[i] <= 0) {
      m[i] = 0;
    } else {
      // Weighted harmonic mean (PCHIP): never overshoots, so radius stays monotone in distance.
      const w1 = 2 * h[i] + h[i - 1];
      const w2 = h[i] + 2 * h[i - 1];
      m[i] = (w1 + w2) / (w1 / delta[i - 1] + w2 / delta[i]);
    }
  }
  // 10 px per metre out of the centre, as the straight-line map had: a UWB friend at 1 m sits where they always did.
  m[0] = delta[0];
  // Flat into the cap, so far friends ease onto the edge instead of stopping dead at 60 m.
  m[n - 1] = 0;
  return m;
})();

export function distanceToRadius(meters: number): number {
  if (!Number.isFinite(meters) || meters <= 0) {
    return 0;
  }
  const last = RADIUS_KNOTS[RADIUS_KNOTS.length - 1];
  if (meters >= last[0]) {
    return last[1];
  }
  let i = 0;
  while (meters > RADIUS_KNOTS[i + 1][0]) {
    i += 1;
  }
  const [x0, y0] = RADIUS_KNOTS[i];
  const [x1, y1] = RADIUS_KNOTS[i + 1];
  const h = x1 - x0;
  const t = (meters - x0) / h;
  const t2 = t * t;
  const t3 = t2 * t;
  return (
    (2 * t3 - 3 * t2 + 1) * y0 +
    (t3 - 2 * t2 + t) * h * RADIUS_TANGENTS[i] +
    (-2 * t3 + 3 * t2) * y1 +
    (t3 - t2) * h * RADIUS_TANGENTS[i + 1]
  );
}

export function radarPlacementMeters(peer: RadarGeometryPeer): number {
  // A Bluetooth-nearby friend sits at their RSSI band's distance (2 / 6 / 15 / 30 m), the same number the ring
  // highlight uses. A fixed 55 m drew a "Right here" friend outside the 30 m ring (2026-10-16 audit). Never a
  // bearing: the bubble has no arrow (radarDirectionWeight is 0 for this kind).
  if (peer.presenceKind === 'ble-in-range') {
    return peer.distanceMeters !== null &&
      Number.isFinite(peer.distanceMeters) &&
      peer.distanceMeters > 0
      ? peer.distanceMeters
      : BLE_IN_RANGE_PLACEHOLDER_METERS;
  }
  if (peer.distanceMeters !== null && Number.isFinite(peer.distanceMeters)) {
    return peer.distanceMeters;
  }
  return BLE_IN_RANGE_PLACEHOLDER_METERS;
}

export function computeScreenPosition(
  peer: RadarGeometryPeer,
  deviceHeadingDeg: number | null,
): { x: number; y: number } {
  const visualRadius = distanceToRadius(radarPlacementMeters(peer));

  if (peer.bearingDeg === null || !Number.isFinite(peer.bearingDeg)) {
    const x = RADAR_CENTER;
    const y = RADAR_CENTER - visualRadius;
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      warnNonFiniteRadarPosition(peer, deviceHeadingDeg, { x, y, visualRadius });
      return safeCenterFallback();
    }
    return { x, y };
  }

  const effectiveHeading =
    deviceHeadingDeg !== null && Number.isFinite(deviceHeadingDeg) ? deviceHeadingDeg : 0;
  const screenAngleDeg = peer.bearingDeg - effectiveHeading;
  const screenAngleRad = ((screenAngleDeg - 90) * Math.PI) / 180;

  const x = RADAR_CENTER + visualRadius * Math.cos(screenAngleRad);
  const y = RADAR_CENTER + visualRadius * Math.sin(screenAngleRad);

  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    warnNonFiniteRadarPosition(peer, deviceHeadingDeg, { x, y, visualRadius });
    return safeCenterFallback();
  }

  return { x, y };
}

/**
 * Grouping by real overlap (Sam, 2026-10-26): with amenities half a face's size on the radar, one fixed gap grouped
 * things that only sat near each other - a friend wore a "2" with a food disc beside, not on, them. Given each bubble's
 * radius, two join only when their centres are closer than this share of their summed radii: they overlap by well over
 * a third, too much to tap apart. Membership is pairwise to any member, not to the group's drifting centre.
 */
export const RADAR_CLUSTER_OVERLAP = 0.7;

export function computeClusters(
  positioned: PositionedRadarPeer[],
  threshold = RADAR_CLUSTER_THRESHOLD,
  radiusOf?: (p: PositionedRadarPeer) => number,
): RadarCluster[] {
  if (radiusOf) return computeOverlapClusters(positioned, radiusOf);
  const clusters: RadarCluster[] = [];
  const assigned = new Set<string>();

  for (const p of positioned) {
    if (assigned.has(p.peer.id)) {
      continue;
    }

    const cluster: RadarCluster = {
      centerX: p.screenX,
      centerY: p.screenY,
      peers: [p],
    };
    assigned.add(p.peer.id);

    for (const q of positioned) {
      if (assigned.has(q.peer.id)) {
        continue;
      }
      const dx = q.screenX - cluster.centerX;
      const dy = q.screenY - cluster.centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < threshold) {
        cluster.peers.push(q);
        assigned.add(q.peer.id);
        cluster.centerX =
          cluster.peers.reduce((sum, pp) => sum + pp.screenX, 0) / cluster.peers.length;
        cluster.centerY =
          cluster.peers.reduce((sum, pp) => sum + pp.screenY, 0) / cluster.peers.length;
      }
    }

    clusters.push(cluster);
  }

  return clusters;
}

function computeOverlapClusters(
  positioned: PositionedRadarPeer[],
  radiusOf: (p: PositionedRadarPeer) => number,
): RadarCluster[] {
  const overlaps = (a: PositionedRadarPeer, b: PositionedRadarPeer) =>
    Math.hypot(a.screenX - b.screenX, a.screenY - b.screenY) <
    (radiusOf(a) + radiusOf(b)) * RADAR_CLUSTER_OVERLAP;
  const clusters: RadarCluster[] = [];
  const assigned = new Set<string>();
  for (const p of positioned) {
    if (assigned.has(p.peer.id)) continue;
    const members = [p];
    assigned.add(p.peer.id);
    // Grow the group through anyone overlapping any member, so a chain of three is one group whatever the order.
    for (let i = 0; i < members.length; i += 1) {
      for (const q of positioned) {
        if (!assigned.has(q.peer.id) && overlaps(members[i], q)) {
          members.push(q);
          assigned.add(q.peer.id);
        }
      }
    }
    clusters.push({
      centerX: members.reduce((sum, m) => sum + m.screenX, 0) / members.length,
      centerY: members.reduce((sum, m) => sum + m.screenY, 0) / members.length,
      peers: members,
    });
  }
  return clusters;
}

export type RingZone = 'inner' | 'mid' | 'outer';

/** Ring boundaries in metres: inner ≤ 5 < mid ≤ 15 < outer. */
export const RING_ZONE_BOUNDARIES_M = [5, 15] as const;
/** A zone change needs the distance this fraction past the boundary, so ±1 m GPS noise at 5 m cannot flicker it. */
export const RING_ZONE_HYSTERESIS = 0.1;

const RING_ZONE_ORDER: RingZone[] = ['inner', 'mid', 'outer'];

function zoneFor(d: number, innerMax: number, midMax: number): RingZone {
  if (d > midMax) {
    return 'outer';
  }
  if (d > innerMax) {
    return 'mid';
  }
  return 'inner';
}

/** The ring a distance lights, holding the previous one until the distance is clearly past a boundary. */
export function ringZoneForDistance(
  distanceMeters: number | null,
  previous: RingZone | null = null,
  hysteresis = RING_ZONE_HYSTERESIS,
): RingZone | null {
  if (distanceMeters === null || !Number.isFinite(distanceMeters)) {
    return null;
  }
  const [a, b] = RING_ZONE_BOUNDARIES_M;
  const plain = zoneFor(distanceMeters, a, b);
  if (previous === null || plain === previous) {
    return plain;
  }
  const rank = RING_ZONE_ORDER.indexOf(previous);
  const outward = zoneFor(distanceMeters, a * (1 + hysteresis), b * (1 + hysteresis));
  if (RING_ZONE_ORDER.indexOf(outward) > rank) {
    return outward;
  }
  const inward = zoneFor(distanceMeters, a * (1 - hysteresis), b * (1 - hysteresis));
  if (RING_ZONE_ORDER.indexOf(inward) < rank) {
    return inward;
  }
  return previous;
}

export function ringHighlightsForZone(zone: RingZone | null): RingHighlights {
  return {
    outer: zone === 'outer' ? 'bright' : 'none',
    mid: zone === 'mid' ? 'bright' : 'none',
    inner: zone === 'inner' ? 'bright' : 'none',
  };
}

export function ringHighlightsFromDistance(distanceMeters: number | null): RingHighlights {
  return ringHighlightsForZone(ringZoneForDistance(distanceMeters));
}

export function clusterIdFromPeers(peers: PositionedRadarPeer[]): string {
  return peers
    .map((p) => p.peer.id)
    .sort()
    .join('|');
}

export function ringRadiusForLevel(
  baseRadius: number,
  level: RingLevel,
  expandedRadius: number,
): number {
  if (level === 'bright') {
    return expandedRadius;
  }
  return baseRadius;
}

export function computeTargetArrow(options: {
  peerScreenX: number;
  peerScreenY: number;
  visualRadius: number;
  screenAngleRad: number;
  show: boolean;
  /** Head length and half-width (default 8 / 6). */
  tipSize?: number;
  tipWidth?: number;
}): TargetArrowGeometry {
  const hidden: TargetArrowGeometry = {
    visible: false,
    x1: RADAR_CENTER,
    y1: RADAR_CENTER,
    x2: RADAR_CENTER,
    y2: RADAR_CENTER,
    tipPath: '',
  };

  if (!options.show) {
    return hidden;
  }

  const computedArrowLength =
    options.visualRadius - RADAR_AVATAR_RADIUS - RADAR_ARROW_START_OFFSET - RADAR_ARROW_END_GAP;
  if (computedArrowLength < RADAR_MIN_ARROW_ROOM) {
    return hidden;
  }

  const arrowLength = Math.max(computedArrowLength, RADAR_MIN_ARROW_LENGTH);
  const ux = Math.cos(options.screenAngleRad);
  const uy = Math.sin(options.screenAngleRad);
  const x1 = RADAR_CENTER + ux * RADAR_ARROW_START_OFFSET;
  const y1 = RADAR_CENTER + uy * RADAR_ARROW_START_OFFSET;
  const x2 = x1 + ux * arrowLength;
  const y2 = y1 + uy * arrowLength;

  const tipSize = options.tipSize ?? 8;
  const tipWidth = options.tipWidth ?? 6;
  const dx = Math.cos(options.screenAngleRad);
  const dy = Math.sin(options.screenAngleRad);
  const backX = x2 - dx * tipSize;
  const backY = y2 - dy * tipSize;
  const perpX = -dy;
  const perpY = dx;
  const backLeftX = backX + perpX * tipWidth;
  const backLeftY = backY + perpY * tipWidth;
  const backRightX = backX - perpX * tipWidth;
  const backRightY = backY - perpY * tipWidth;

  return {
    visible: true,
    x1,
    y1,
    x2,
    y2,
    // The dashed stem stops where the head begins (Sam, 2026-10-26: the stem ran through the head and showed through it).
    stemX2: backX,
    stemY2: backY,
    tipPath: `M ${backLeftX} ${backLeftY} L ${x2} ${y2} L ${backRightX} ${backRightY} Z`,
  };
}

/**
 * 0..1 how much of the arrow fits between the centre dot and the bubble at this radius. The arrow fades out as a
 * friend closes in, instead of vanishing the frame its length dips under {@link RADAR_MIN_ARROW_ROOM}.
 */
export function targetArrowRoomFactor(visualRadius: number): number {
  const room =
    visualRadius -
    RADAR_AVATAR_RADIUS -
    RADAR_ARROW_START_OFFSET -
    RADAR_ARROW_END_GAP -
    RADAR_MIN_ARROW_ROOM;
  return Math.min(1, Math.max(0, room / 10));
}

export function screenAngleRadForPeer(
  peer: RadarGeometryPeer,
  deviceHeadingDeg: number | null,
): number {
  if (peer.bearingDeg === null) {
    return (-90 * Math.PI) / 180;
  }
  const effectiveHeading = deviceHeadingDeg ?? 0;
  const screenAngleDeg = peer.bearingDeg - effectiveHeading;
  return ((screenAngleDeg - 90) * Math.PI) / 180;
}

export function shouldShowTargetArrow(peer: RadarGeometryPeer): boolean {
  const placementMeters = radarPlacementMeters(peer);
  const minDistance = peer.presenceKind === 'uwb-live' ? 0.3 : 2;
  return (
    (peer.presenceKind === 'uwb-live' ||
      peer.presenceKind === 'gps-live' ||
      peer.presenceKind === 'gps-stale') &&
    placementMeters > minDistance &&
    peer.bearingDeg !== null
  );
}

/** Below this uncertainty a bearing is drawn in full; above {@link BEARING_FADE_END_DEG} it is not drawn at all. */
export const BEARING_FADE_START_DEG = 35;
export const BEARING_FADE_END_DEG = 55;

function bearingUncertaintyForPeer(peer: RadarGeometryPeer): number | null {
  if (peer.bearingUncertaintyDeg != null && Number.isFinite(peer.bearingUncertaintyDeg)) {
    return peer.bearingUncertaintyDeg;
  }
  if (peer.presenceKind === 'uwb-live' && peer.directionAvailable === true) {
    return 0;
  }
  // Restored snapshot rows carry the fix error but not the uncertainty: derive it the same way the display does.
  const acc = peer.horizontalAccuracyMeters;
  if (acc == null || !Number.isFinite(acc)) {
    return null;
  }
  const d = peer.distanceMeters;
  if (d == null || !Number.isFinite(d) || d <= 0) {
    return 90;
  }
  return (Math.atan(acc / d) * 180) / Math.PI;
}

/**
 * How much to trust the peer's bearing, 0..1. 1 up to 35° of uncertainty, 0 past 55°, smooth between - so the
 * direction (arrow, bubble angle) fades out around 45° instead of a 12 m GPS fix flipping a friend in the same car
 * from ahead to behind every update (2026-10-14). Unknown uncertainty is trusted, as before.
 */
export function radarDirectionWeight(peer: RadarGeometryPeer): number {
  if (peer.bearingDeg === null || !Number.isFinite(peer.bearingDeg)) {
    return 0;
  }
  if (peer.presenceKind === 'ble-in-range' || peer.presenceKind === 'none') {
    return 0;
  }
  const u = bearingUncertaintyForPeer(peer);
  if (u === null) {
    return 1;
  }
  const t = (u - BEARING_FADE_START_DEG) / (BEARING_FADE_END_DEG - BEARING_FADE_START_DEG);
  const c = Math.min(1, Math.max(0, t));
  return 1 - c * c * (3 - 2 * c);
}
